import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "sonner";
import { ApiError, getJson, SessionExpiredError } from "@/lib/api-client";
import { zipKeys } from "@/lib/query";
import {
  type CreateZipRequest,
  createZipResponseSchema,
  type ZipJob,
  zipJobListResponseSchema,
} from "@/schemas/zips";

/** While a job is being built: 2 s, backing off to 5 s once it has run for 30 s. */
export const ACTIVE_POLL_MS = 2000;
export const ACTIVE_BACKOFF_POLL_MS = 5000;
export const BACKOFF_AFTER_MS = 30_000;
const IDLE_POLL_MS = 30_000;

/** How long to wait before asking again, given the jobs and the time they have been active. */
export function pollInterval(jobs: ZipJob[] | undefined, activeSince: number | null, now: number) {
  if (!jobs?.some(isActive)) return IDLE_POLL_MS;
  return activeSince !== null && now - activeSince >= BACKOFF_AFTER_MS
    ? ACTIVE_BACKOFF_POLL_MS
    : ACTIVE_POLL_MS;
}

export function isActive(job: ZipJob): boolean {
  return job.status === "queued" || job.status === "running";
}

/** This account's downloads. Polls every 2 s while one is being built (5 s after 30 s), rarely otherwise. */
export function useZipJobs() {
  const activeSince = useRef<number | null>(null);
  return useQuery({
    queryKey: zipKeys.list,
    queryFn: async () => (await getJson("/api/zips", zipJobListResponseSchema)).data,
    refetchInterval: (query) => {
      const now = Date.now();
      const active = query.state.data?.some(isActive) ?? false;
      if (!active) activeSince.current = null;
      else activeSince.current ??= now;
      return pollInterval(query.state.data, activeSince.current, now);
    },
    staleTime: 0,
  });
}

export function useCreateZip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateZipRequest) => {
      const res = await fetch("/api/zips", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      if (res.status === 401) throw new SessionExpiredError();
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const message =
          typeof body === "object" && body !== null && "error" in body
            ? String((body as { error: unknown }).error)
            : `The server answered ${res.status}`;
        throw new ApiError(message, res.status);
      }
      return createZipResponseSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: zipKeys.all });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Could not start the download");
    },
  });
}
